import {ExternalLink, Scale, Star} from 'lucide-react'
import { Link } from "react-router-dom"
import Breadcrumbs from "@/components/seo/Breadcrumbs"
import JsonLd from "@/components/seo/JsonLd"
import MedicalDisclaimer from "@/components/seo/MedicalDisclaimer"
import SeoHead, { SITE_NAME, SITE_URL } from "@/components/seo/SeoHead"
import { comparisonGuides } from "@/data/comparisons"

function Comparison() {
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Comparison Guides", item: `${SITE_URL}/comparison` }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Vitamin Comparison Guides",
      url: `${SITE_URL}/comparison`,
      isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_URL },
      mainEntity: {
        "@type": "ItemList",
        itemListElement: comparisonGuides.map((guide, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `${SITE_URL}/comparison/${guide.slug}`,
          name: guide.title
        }))
      }
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-teal-50">
      <SeoHead
        title="Vitamin Comparison Guides | Vitamin Vital Portal"
        description="Compare Arko Vital vs B Vital and other energy supplements. See ratings, prices, and feature tables on Vitamin Vital Portal."
        path="/comparison"
        type="website"
      />
      <JsonLd data={schema} />

      <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-6 py-2 mb-6">
              <Scale className="w-5 h-5" />
              <span className="text-sm font-medium">Head-to-Head Comparison</span>
            </div>
            <h1 className="text-5xl font-bold mb-4">Vitamin Comparison Guides</h1>
            <p className="text-xl text-teal-100 max-w-3xl mx-auto">
              Side-by-side supplement comparisons with prices, ratings, and use-case verdicts
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Comparison Guides" }]} />

        <div className="space-y-12">
          {comparisonGuides.map((guide) => (
            <article key={guide.slug} className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="p-8 border-b border-gray-100">
                <h2 className="text-3xl font-bold text-gray-900 mb-3">{guide.title}</h2>
                <p className="text-gray-600 mb-6">{guide.subtitle}</p>
                <Link
                  to={`/comparison/${guide.slug}`}
                  className="inline-flex items-center bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-full font-semibold transition-colors"
                >
                  Read full comparison
                </Link>
              </div>

              <div className="grid md:grid-cols-2 gap-8 p-8">
                {guide.products.map((product) => (
                  <div key={product.name}>
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-32 h-32 object-cover rounded-xl mb-4"
                    />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{product.name}</h3>
                    <div className="flex items-center gap-2 mb-2">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      <span className="font-semibold text-gray-900">{product.rating}</span>
                    </div>
                    <div className="text-2xl font-bold text-teal-600 mb-3">{product.price}</div>
                    <p className="text-sm text-gray-600 mb-4">{product.overall}</p>
                    <a
                      href={product.affiliateUrl}
                      className="inline-flex items-center gap-2 text-orange-600 font-semibold hover:text-orange-700"
                    >
                      Check Best Price
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                ))}
              </div>

              <div className="overflow-x-auto border-t border-gray-100">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Feature</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">{guide.leftLabel}</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">{guide.rightLabel}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {guide.features.slice(0, 6).map((row) => (
                      <tr key={row.feature}>
                        <td className="px-6 py-3 text-sm font-medium text-gray-900">{row.feature}</td>
                        <td className="px-6 py-3 text-sm text-gray-700">
                          {typeof row.left === "boolean" ? (row.left ? "Yes" : "No") : row.left}
                        </td>
                        <td className="px-6 py-3 text-sm text-gray-700">
                          {typeof row.right === "boolean" ? (row.right ? "Yes" : "No") : row.right}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-12">
          <MedicalDisclaimer extra="Vitamin Vital Portal may earn affiliate commissions from purchases made through links on this page." />
        </div>
      </div>
    </div>
  )
}

export default Comparison
